import { AlertCircle } from "lucide-react";
import { useCallback, useMemo } from "react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { useAppShellBreadcrumb } from "@/shared/components/layout/useAppShellBreadcrumb";
import { NoAccessView } from "@/shared/components/NoAccessView";
import useTranslations from "@/shared/lang/useTranslations";

import { SwapForm } from "../components/SwapForm";
import { SwapsHeader } from "../components/SwapsHeader";
import { SwapSuccessScreen } from "../components/SwapSuccessScreen";
import { useSwaps } from "../hooks/useSwaps";

export const SwapsPage = () => {
  const { t } = useTranslations("swaps");
  const { t: tGlobal } = useTranslations("global");
  const breadcrumb = useMemo(
    () => [
      { label: tGlobal("sidebar_section_operaciones") },
      { label: tGlobal("sidebar_menu_swaps"), to: "/app/swaps" },
    ],
    [tGlobal],
  );
  useAppShellBreadcrumb(breadcrumb);

  // `autoFetch` belongs to the page and ONLY the page: `SwapForm` calls this hook too, and if it fetched on
  // mount the loading gate below would unmount it mid-flight, remount it, and loop forever.
  const {
    hasAccess,
    walletsLoading,
    walletsLoaded,
    walletsError,
    fetchWallets,
    success,
    submitSwap,
    clearSuccess,
  } = useSwaps({ autoFetch: true });

  // The POST lives in a thunk (`thunkSubmitSwap`), not here: it owns the idempotency key, captures the
  // pre-swap balances and refreshes the wallet cache, all of which must survive a re-render.
  const handleConfirmSwap = useCallback(async () => {
    await submitSwap();
  }, [submitSwap]);

  const handleNewSwap = useCallback(() => {
    clearSuccess();
  }, [clearSuccess]);

  // Access is decided by the backend RBAC (`payments:read`/`payments:write`); a denial latches for the
  // session in the thunk rather than being re-tried on every mount.
  if (!hasAccess) {
    return <NoAccessView />;
  }

  // Only the FIRST load takes over the screen. A background refresh (e.g. the one a completed swap triggers)
  // must leave whatever is on screen mounted — swapping it for a spinner and back is what caused the loop.
  const isFirstLoad = walletsLoading && !walletsLoaded;

  return (
    // A swap is a single focused form, not a dashboard: it reads better at the wizard's column width than
    // stretched across the full 1400px shell. Same measure as the dispersions wizard.
    <div className="mx-auto w-full max-w-[720px] space-y-6 p-2">
      <SwapsHeader />

      {isFirstLoad ? (
        <div className="flex items-center justify-center py-12">
          <div className="flex flex-col items-center gap-3">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
            <span className="text-sm text-muted-foreground">{t("loading_wallets")}</span>
          </div>
        </div>
      ) : walletsError ? (
        <ColocaAlert
          variant="destructive"
          icon={AlertCircle}
          text={walletsError}
          actions={
            <ColocaButton variant="secondary" size="sm" onClick={() => void fetchWallets()}>
              {t("retry")}
            </ColocaButton>
          }
        />
      ) : success.isVisible ? (
        <SwapSuccessScreen
          oldBalances={success.oldBalances}
          newBalances={success.newBalances}
          summary={success.summary}
          onNewSwap={handleNewSwap}
        />
      ) : (
        <SwapForm onConfirm={handleConfirmSwap} />
      )}
    </div>
  );
};
